import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { hasValue } from '../../../navigator/util';
const Stack = styled.div `
  position: fixed;
  right: 2.1vw;
  bottom: 7.1rem;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1.75rem;
  z-index: 2;
  pointer-events: none;
  isolation: isolate;
  padding: 0.55rem 0.25rem 0.7rem 1.35rem;

  &::before {
    content: '';
    position: absolute;
    top: -1.6rem;
    right: -4.2vw;
    bottom: -1.4rem;
    left: -5.2rem;
    z-index: -1;
    background: linear-gradient(
      270deg,
      rgba(4, 8, 14, 0.82) 0%,
      rgba(5, 9, 15, 0.72) 18%,
      rgba(7, 11, 17, 0.54) 40%,
      rgba(9, 13, 20, 0.22) 62%,
      rgba(9, 13, 20, 0.06) 80%,
      rgba(9, 13, 20, 0) 100%
    );
    filter: blur(18px);
    opacity: 1;
  }

  @media (orientation: portrait) {
    right: 1.5rem;
    bottom: 8.2rem;
    gap: 1.95rem;
    padding: 0.65rem 0.3rem 0.8rem 1.5rem;

    &::before {
      top: -1.75rem;
      right: -2.4rem;
      bottom: -1.5rem;
      left: -5.8rem;
    }
  }

  @media (max-width: 768px) {
    right: 1.2rem;
    bottom: 6rem;
    gap: 1.35rem;
    padding: 0.55rem 0.2rem 0.65rem 1rem;

    &::before {
      top: -1rem;
      right: -1.8rem;
      bottom: -0.9rem;
      left: -3.8rem;
      filter: blur(14px);
    }
  }
`;
const Item = styled.div `
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;
  min-width: clamp(8rem, 10vw, 11rem);
  max-width: clamp(11rem, 16vw, 16rem);
`;
const Label = styled.div `
  color: rgba(181, 190, 202, 0.82);
  font-family: 'Sora', sans-serif;
  font-size: clamp(0.7rem, 0.74vw, 0.82rem);
  font-weight: 400;
  line-height: 1.15;
  margin-bottom: 0.24rem;
  max-width: 100%;
  overflow-wrap: anywhere;
  text-wrap: balance;
`;
const ValueRow = styled.div `
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.38rem;
  color: #f5f7fb;
  font-family: 'Sora', sans-serif;
  font-size: clamp(0.95rem, 1.08vw, 1.14rem);
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -0.02em;
  max-width: 100%;
  text-align: right;
  overflow-wrap: anywhere;
`;
const Icon = styled.img `
  width: clamp(0.95rem, 1vw, 1.12rem);
  height: clamp(0.95rem, 1vw, 1.12rem);
  opacity: 0.92;
  flex: 0 0 auto;
`;
const toTranslationKey = (value) => value.toLowerCase().replace(/\s+/g, '-');
const formatValue = (value, suffix = '') => {
    if (!hasValue(value))
        return null;
    const trimmed = String(value).trim();
    if (!suffix || trimmed.includes(suffix))
        return trimmed;
    return `${trimmed}${suffix}`;
};
const FloorTelemetry = ({ showAirQuality, showCO2, showFloorOccupancy, showHumidity, showTemperature, airQuality, ambientTemp, co2, occupancyStatus, relativeHumidity, }) => {
    const { t } = useTranslation();
    const rows = [];
    if (showFloorOccupancy && hasValue(occupancyStatus)) {
        rows.push({
            id: 'floor-occupancy',
            icon: require('../../../../../assets/images/floor-telemetry/floor-occupancy.svg'),
            label: t('common.floor-occupancy'),
            value: t(`current-occupancy.${toTranslationKey(String(occupancyStatus))}`),
        });
    }
    if (showAirQuality && hasValue(airQuality)) {
        rows.push({
            id: 'floor-iaq',
            icon: require('../../../../../assets/images/floor-telemetry/air-quality.svg'),
            label: t('common.iaq'),
            value: t(`common.${toTranslationKey(String(airQuality))}`),
        });
    }
    if (showCO2 && hasValue(co2)) {
        rows.push({
            id: 'floor-co2',
            icon: require('../../../../../assets/images/floor-telemetry/co2-level.svg'),
            label: t('common.co2'),
            value: t(`common.${toTranslationKey(String(co2))}`),
        });
    }
    if (showTemperature) {
        const temperature = formatValue(ambientTemp);
        if (temperature) {
            rows.push({
                id: 'floor-temperature',
                icon: require('../../../../../assets/images/floor-telemetry/temperature.svg'),
                label: t('common.temperature'),
                value: temperature,
            });
        }
    }
    if (showHumidity) {
        const humidity = formatValue(relativeHumidity, '%');
        if (humidity) {
            rows.push({
                id: 'floor-humidity',
                icon: require('../../../../../assets/images/floor-telemetry/humidity.svg'),
                label: t('common.humidity'),
                value: humidity,
            });
        }
    }
    if (rows.length === 0)
        return null;
    return (_jsx(Stack, Object.assign({ "aria-label": "Floor telemetry" }, { children: rows.map((row) => (_jsxs(Item, Object.assign({ id: row.id }, { children: [_jsx(Label, Object.assign({ title: row.label }, { children: row.label })), _jsxs(ValueRow, { children: [_jsx(Icon, { src: row.icon, alt: "", "aria-hidden": "true" }), _jsx("span", Object.assign({ title: row.value }, { children: row.value }))] })] }), row.id))) })));
};
export default FloorTelemetry;
